import React, { useState, useEffect } from 'react';
import { Award, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_CONFIG = {
  bronce: {
    label: 'Bronce',
    gradient: 'linear-gradient(135deg, #8B4513 0%, #CD853F 50%, #A0522D 100%)',
    textColor: '#FFF3E0',
    accent: '#FFE0B2',
    perk: '3% cashback',
  },
  plata: {
    label: 'Plata',
    gradient: 'linear-gradient(135deg, #B0B0B0 0%, #E8E8E8 50%, #9E9E9E 100%)',
    textColor: '#1A1A1A',
    accent: '#424242',
    perk: '5% descuento',
  },
  oro: {
    label: 'Oro',
    gradient: 'linear-gradient(135deg, #B8860B 0%, #FFD700 50%, #DAA520 100%)',
    textColor: '#1A1A1A',
    accent: '#5D4037',
    perk: '10% descuento',
  },
  platino: {
    label: 'Platino',
    gradient: 'linear-gradient(135deg, #3B4252 0%, #E5E9F0 50%, #4C566A 100%)',
    textColor: '#1A1A1A',
    accent: '#2E3440',
    perk: '20% descuento',
  },
  pro: {
    label: 'Pro',
    gradient: 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 50%, #000000 100%)',
    textColor: '#D4C5B0',
    accent: '#D4C5B0',
    perk: '30% descuento',
  },
};

export default function LoyaltyCard({ user }) {
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLoyalty = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${BACKEND_URL}/api/user/loyalty`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setLoyalty(data);
        }
      } catch (e) {
        console.error('Error fetching loyalty:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchLoyalty();
  }, []);

  if (loading || !loyalty) return null;

  const config = TIER_CONFIG[loyalty.tier] || TIER_CONFIG.bronce;
  const discountPct = Math.round((loyalty.discount_rate || 0) * 100);
  const cycleLabel = `1 Abr ${loyalty.cycle_start_year} – 31 Mar ${loyalty.cycle_start_year + 1}`;

  return (
    <div
      className="relative overflow-hidden rounded-lg p-6 shadow-xl"
      style={{
        background: config.gradient,
        color: config.textColor,
        minHeight: '200px',
      }}
      data-testid="loyalty-card"
    >
      {/* Decorative circle */}
      <div
        className="absolute -right-16 -top-16 w-48 h-48 rounded-full opacity-10"
        style={{ background: config.textColor }}
      />

      <div className="relative z-10 flex justify-between items-start mb-4">
        <div>
          <p className="text-xs uppercase tracking-widest opacity-80">ArtFitz Club</p>
          <h3
            className="text-3xl font-bold mt-1"
            style={{ fontFamily: 'Playfair Display' }}
            data-testid="loyalty-card-tier"
          >
            {config.label}
          </h3>
        </div>
        <Award className="w-8 h-8 opacity-80" />
      </div>

      <div className="relative z-10 mb-4">
        <p className="text-sm opacity-80">Titular</p>
        <p className="text-lg font-semibold truncate" data-testid="loyalty-card-name">
          {user?.name || user?.email || ''}
        </p>
      </div>

      <div className="relative z-10 grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="opacity-70 text-xs">Fitz Points</p>
          <p className="text-xl font-bold" data-testid="loyalty-card-points">
            {loyalty.fitz_points.toFixed(0)}
          </p>
        </div>
        <div className="text-right">
          <p className="opacity-70 text-xs">Recompensa</p>
          <p className="text-xl font-bold flex items-center justify-end gap-1">
            <Sparkles className="w-4 h-4" /> {config.perk}
          </p>
        </div>
      </div>

      {loyalty.next_tier && loyalty.next_tier_threshold && (
        <div className="relative z-10 mt-4" data-testid="loyalty-card-progress">
          <div className="flex justify-between text-xs mb-1">
            <span className="opacity-80">
              Próximo: {loyalty.next_tier.charAt(0).toUpperCase() + loyalty.next_tier.slice(1)}
            </span>
            <span className="opacity-80">
              {loyalty.points_to_next.toFixed(0)} pts para subir
            </span>
          </div>
          <div className="h-2 bg-black bg-opacity-20 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${loyalty.progress}%`,
                background: config.accent,
              }}
            />
          </div>
        </div>
      )}

      <div className="relative z-10 mt-4 pt-3 border-t border-white/20 text-xs opacity-80">
        <p>Ciclo: {cycleLabel}</p>
        {loyalty.expires_at && (
          <p data-testid="loyalty-card-expires">
            Válido hasta: {new Date(loyalty.expires_at).toLocaleDateString('es-ES')}
          </p>
        )}
      </div>
    </div>
  );
}

// Export discount rate helper for checkout use
export { TIER_CONFIG };
